import { useEffect, useMemo, useRef, useState } from "react";
import Alert from "../../components/common/Alert";
import Badge from "../../components/common/Badge";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Checkbox from "../../components/common/Checkbox";
import Input from "../../components/common/Input";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Pagination from "../../components/common/Pagination";
import Select from "../../components/common/Select";
import Table from "../../components/common/Table";
import ActionButton from "../../components/common/ActionButton";
import { useStore } from "../../context/StoreContext";
import {
  useCreateDiscountRuleMutation,
  useGetDiscountRulesQuery,
  useGetItemsQuery,
  useUpdateDiscountRuleMutation,
  useUpdateDiscountRuleStatusMutation,
} from "../../store/api";

const DISCOUNT_SCOPES = {
  PRODUCT: "PRODUCT",
  STORE_WIDE: "STORE_WIDE",
};

const DISCOUNT_TYPES = {
  PERCENTAGE: "PERCENTAGE",
  FIXED_AMOUNT: "FIXED_AMOUNT",
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

const initialForm = {
  name: "",
  discountScope: DISCOUNT_SCOPES.PRODUCT,
  itemId: "",
  discountType: DISCOUNT_TYPES.PERCENTAGE,
  value: "",
  minOrderTotal: "",
  isActive: true,
  startsAt: "",
  endsAt: "",
};

const ERROR_MESSAGES = {
  STORE_NOT_FOUND: "Selected store was not found. Refresh and try again.",
  DISCOUNT_RULE_NOT_FOUND: "Discount rule was not found. Refresh the list.",
  DISCOUNT_RULE_NAME_REQUIRED: "Enter a discount name.",
  DISCOUNT_RULE_SCOPE_INVALID: "Select Product or Store-wide scope.",
  DISCOUNT_RULE_TYPE_INVALID: "Select Percentage or Fixed Amount.",
  DISCOUNT_RULE_VALUE_INVALID: "Discount value must be greater than zero.",
  DISCOUNT_RULE_PERCENTAGE_INVALID: "Percentage discount cannot exceed 100%.",
  DISCOUNT_RULE_DATE_RANGE_INVALID: "End date must be on or after start date.",
  DISCOUNT_RULE_MIN_TOTAL_INVALID: "Minimum order total cannot be negative.",
  DISCOUNT_RULE_ITEM_REQUIRED: "Select a product for product discount.",
  DISCOUNT_RULE_ITEM_NOT_ALLOWED: "Store-wide discount cannot target a product.",
  DISCOUNT_RULE_ITEM_NOT_FOUND: "Selected product does not belong to this store.",
  DISCOUNT_RULE_ITEM_DELETED: "Deleted products cannot be discounted.",
  DISCOUNT_RULE_OVERLAP: "Another active rule already exists for this target and date range.",
};

const getRows = (data) => {
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;
  return [];
};

const getTotalCount = (data, fallback) => Number(data?.totalCount ?? fallback) || 0;
const getRuleId = (rule) => rule?.discountId || rule?.id;
const getItemId = (item) => item?.itemId || item?.id;
const getItemName = (item) => item?.name || item?.itemName || "Unnamed item";

const getErrorMessage = (error, fallback) => {
  const data = error?.data || {};
  const code = data.code || data.errorCode || data.title;
  return ERROR_MESSAGES[code] || data.detail || data.message || fallback;
};

const optionalNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const toDatePart = (value) => {
  if (!value) return "";
  const stringValue = String(value);
  const isoMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  const displayMatch = stringValue.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (displayMatch) return `${displayMatch[3]}-${displayMatch[2]}-${displayMatch[1]}`;
  return stringValue.slice(0, 10);
};

const toApiStartDateTime = (value) => {
  const datePart = toDatePart(value);
  return datePart ? new Date(`${datePart}T00:00:00`).toISOString() : null;
};

const toApiEndDateTime = (value) => {
  const datePart = toDatePart(value);
  return datePart ? new Date(`${datePart}T23:59:59.999`).toISOString() : null;
};

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (part) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const formatDate = (value) => {
  if (!value) return "Always";
  const date = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
};

const formatMoney = (value, currencyCode = "USD") => {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currencyCode} ${amount.toFixed(2)}`;
  }
};

const formatRuleValue = (rule, currencyCode) => {
  const value = Number(rule?.value || 0);
  if (rule?.discountType === DISCOUNT_TYPES.PERCENTAGE) {
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  }
  return formatMoney(value, currencyCode);
};

const toForm = (rule = {}) => ({
  name: rule.name || "",
  discountScope: rule.discountScope || DISCOUNT_SCOPES.PRODUCT,
  itemId: rule.itemId || "",
  discountType:
    rule.discountType === "FIXED" || rule.discountType === "AMOUNT"
      ? DISCOUNT_TYPES.FIXED_AMOUNT
      : rule.discountType || DISCOUNT_TYPES.PERCENTAGE,
  value: rule.value ?? "",
  minOrderTotal: rule.minOrderTotal ?? "",
  isActive: rule.isActive ?? true,
  startsAt: toDateInputValue(rule.startsAt),
  endsAt: toDateInputValue(rule.endsAt),
});

const toPayload = (form) => ({
  name: form.name.trim(),
  discountScope: form.discountScope,
  itemId: form.discountScope === DISCOUNT_SCOPES.PRODUCT ? form.itemId : null,
  discountType: form.discountType,
  value: Number(form.value),
  minOrderTotal: optionalNumber(form.minOrderTotal),
  isActive: Boolean(form.isActive),
  startsAt: toApiStartDateTime(form.startsAt),
  endsAt: toApiEndDateTime(form.endsAt),
});

const validateForm = (form) => {
  if (!form.name.trim()) return "Enter a discount name.";
  if (form.discountScope === DISCOUNT_SCOPES.PRODUCT && !form.itemId) {
    return "Select a product for product discount.";
  }
  const value = Number(form.value);
  if (!Number.isFinite(value) || value <= 0) {
    return "Discount value must be greater than zero.";
  }
  if (form.discountType === DISCOUNT_TYPES.PERCENTAGE && value > 100) {
    return "Percentage discount cannot exceed 100%.";
  }
  const minOrderTotal = optionalNumber(form.minOrderTotal);
  if (minOrderTotal !== null && minOrderTotal < 0) {
    return "Minimum order total cannot be negative.";
  }
  if (toDatePart(form.startsAt) && toDatePart(form.endsAt) && toDatePart(form.endsAt) < toDatePart(form.startsAt)) {
    return "End date must be on or after start date.";
  }
  return "";
};

const ProductSearchSelect = ({ items, value, onChange }) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedItem = useMemo(
    () => items.find((item) => String(getItemId(item)) === String(value)),
    [items, value],
  );

  useEffect(() => {
    if (!isOpen) {
      setSearch(selectedItem ? getItemName(selectedItem) : "");
    }
  }, [isOpen, selectedItem]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  const filteredItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return items;
    return items.filter((item) => getItemName(item).toLowerCase().includes(normalizedSearch));
  }, [items, search]);

  const selectItem = (item) => {
    const nextValue = getItemId(item);
    onChange({ target: { name: "itemId", value: nextValue } });
    setSearch(getItemName(item));
    setIsOpen(false);
  };

  return (
    <label ref={containerRef} className="relative flex min-w-0 flex-col gap-2 text-sm text-slate-600">
      Product
      <input
        className="h-11 w-full min-w-0 rounded-lg border border-slate-200 px-4 pr-10 text-base text-slate-900 shadow-sm outline-none transition focus:border-slate-400"
        type="search"
        value={search}
        placeholder="Select product"
        autoComplete="off"
        onChange={(event) => {
          setSearch(event.target.value);
          setIsOpen(true);
          if (value) {
            onChange({ target: { name: "itemId", value: "" } });
          }
        }}
        onFocus={() => setIsOpen(true)}
      />
      <span className="pointer-events-none absolute right-4 top-10 text-slate-400">v</span>
      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-64 overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {filteredItems.length ? (
            filteredItems.map((item) => {
              const itemId = getItemId(item);
              const isSelected = String(itemId) === String(value);
              return (
                <button
                  key={itemId}
                  type="button"
                  className={[
                    "flex w-full items-center px-4 py-2 text-left text-sm transition",
                    isSelected
                      ? "bg-indigo-600 text-white"
                      : "text-slate-900 hover:bg-slate-100",
                  ].join(" ")}
                  onClick={() => selectItem(item)}
                >
                  {getItemName(item)}
                </button>
              );
            })
          ) : (
            <div className="px-4 py-3 text-sm text-slate-500">No products found</div>
          )}
        </div>
      )}
    </label>
  );
};

const DiscountRuleForm = ({
  form,
  setForm,
  items,
  currencyCode,
  validationError,
  mutationState,
  onSubmit,
  onCancel,
  isEditing,
}) => {
  const change = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => {
      const next = {
        ...current,
        [name]: type === "checkbox" ? checked : value,
      };
      if (name === "discountScope" && value === DISCOUNT_SCOPES.STORE_WIDE) {
        next.itemId = "";
      }
      if (name === "discountType" && value === DISCOUNT_TYPES.PERCENTAGE && Number(next.value) > 100) {
        next.value = "100";
      }
      return next;
    });
  };

  const isProductScope = form.discountScope === DISCOUNT_SCOPES.PRODUCT;

  return (
    <form className="space-y-5" onSubmit={onSubmit}>
      <Input label="Name" name="name" value={form.name} onChange={change} required />

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-slate-600">Scope</legend>
        <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-100 p-1">
          {[
            { value: DISCOUNT_SCOPES.PRODUCT, label: "Product" },
            { value: DISCOUNT_SCOPES.STORE_WIDE, label: "Store-wide" },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => change({ target: { name: "discountScope", value: option.value } })}
              className={[
                "h-10 rounded-md text-sm font-semibold transition",
                form.discountScope === option.value
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-900",
              ].join(" ")}
            >
              {option.label}
            </button>
          ))}
        </div>
      </fieldset>

      {isProductScope && (
        <ProductSearchSelect items={items} value={form.itemId} onChange={change} />
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Select label="Discount type" name="discountType" value={form.discountType} onChange={change}>
          <option value={DISCOUNT_TYPES.PERCENTAGE}>Percentage</option>
          {/* <option value={DISCOUNT_TYPES.FIXED_AMOUNT}>Fixed Amount</option> */}
        </Select>
        <Input
          label="Value"
          name="value"
          type="number"
          min="0"
          max={form.discountType === DISCOUNT_TYPES.PERCENTAGE ? "100" : undefined}
          value={form.value}
          onChange={change}
          required
          rightElement={
            <span className="text-sm font-semibold text-slate-500">
              {form.discountType === DISCOUNT_TYPES.PERCENTAGE ? "%" : currencyCode}
            </span>
          }
        />
        <Input
          label="Minimum Order Total"
          name="minOrderTotal"
          type="number"
          min="0"
          value={form.minOrderTotal}
          onChange={change}
        />
        <div className="flex items-end pb-2">
          <Checkbox label="Active" name="isActive" checked={form.isActive} onChange={change} />
        </div>
        <Input label="Start Date" name="startsAt" type="date" value={form.startsAt} onChange={change} />
        <Input label="End Date" name="endsAt" type="date" value={form.endsAt} onChange={change} />
      </div>

      {validationError && <Alert tone="error">{validationError}</Alert>}
      {mutationState.isError && (
        <Alert tone="error">
          {getErrorMessage(mutationState.error, "Failed to save discount rule.")}
        </Alert>
      )}

      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={mutationState.isLoading}>
          Cancel
        </Button>
        <Button type="submit" disabled={mutationState.isLoading}>
          {mutationState.isLoading ? "Saving..." : isEditing ? "Update rule" : "Create rule"}
        </Button>
      </div>
    </form>
  );
};

const DiscountRules = () => {
  const { selectedStoreId } = useStore();
  const [filters, setFilters] = useState({
    scope: "",
    itemId: "",
    isActive: "",
    currentlyEffective: "",
    search: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [form, setForm] = useState(initialForm);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formError, setFormError] = useState("");
  const [statusError, setStatusError] = useState("");

  const queryArgs = useMemo(
    () => ({
      storeId: selectedStoreId,
      scope: filters.scope || undefined,
      itemId: filters.itemId || undefined,
      isActive: filters.isActive === "" ? undefined : filters.isActive === "true",
      currentlyEffective:
        filters.currentlyEffective === "" ? undefined : filters.currentlyEffective === "true",
      page,
      pageSize,
    }),
    [filters.currentlyEffective, filters.isActive, filters.itemId, filters.scope, page, pageSize, selectedStoreId],
  );

  const { data, isLoading, isFetching, isError, error } = useGetDiscountRulesQuery(queryArgs, {
    skip: !selectedStoreId,
  });
  const { data: itemsData, isLoading: itemsLoading } = useGetItemsQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });
  const [createDiscountRule, createState] = useCreateDiscountRuleMutation();
  const [updateDiscountRule, updateState] = useUpdateDiscountRuleMutation();
  const [updateDiscountRuleStatus, statusState] = useUpdateDiscountRuleStatusMutation();

  const items = useMemo(() => (Array.isArray(itemsData) ? itemsData : []), [itemsData]);
  const itemNameById = useMemo(
    () => new Map(items.map((item) => [String(getItemId(item)), getItemName(item)])),
    [items],
  );
  const allRows = getRows(data);
  const search = filters.search.trim().toLowerCase();
  const rowsForDisplay = search
    ? allRows.filter((rule) => {
        const haystack = [
          rule?.name,
          rule?.discountScope,
          rule?.discountType,
          rule?.itemId ? itemNameById.get(String(rule.itemId)) : "",
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(search);
      })
    : allRows;
  const totalCount = search ? rowsForDisplay.length : getTotalCount(data, allRows.length);
  const totalPages = Math.max(1, Math.ceil(totalCount / Math.max(1, pageSize)));
  const currencyCode = data?.currencyCode || data?.storeCurrencyCode || "USD";

  const setFilterValue = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ scope: "", itemId: "", isActive: "", currentlyEffective: "", search: "" });
    setPage(1);
  };

  const openCreate = () => {
    setForm(initialForm);
    setEditingRuleId(null);
    setFormError("");
    setIsFormOpen(true);
    createState.reset?.();
    updateState.reset?.();
  };

  const openEdit = (rule) => {
    setForm(toForm(rule));
    setEditingRuleId(getRuleId(rule));
    setFormError("");
    setIsFormOpen(true);
    createState.reset?.();
    updateState.reset?.();
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingRuleId(null);
    setForm(initialForm);
    setFormError("");
  };

  const saveRule = async (event) => {
    event.preventDefault();
    const validationMessage = validateForm(form);
    setFormError(validationMessage);
    if (validationMessage) return;

    try {
      if (editingRuleId) {
        await updateDiscountRule({
          storeId: selectedStoreId,
          discountId: editingRuleId,
          payload: toPayload(form),
        }).unwrap();
      } else {
        await createDiscountRule({ storeId: selectedStoreId, payload: toPayload(form) }).unwrap();
      }
      closeForm();
    } catch {
      // Mutation error is rendered inside the form.
    }
  };

  const toggleRuleStatus = async (rule) => {
    setStatusError("");
    try {
      await updateDiscountRuleStatus({
        storeId: selectedStoreId,
        discountId: getRuleId(rule),
        isActive: !rule.isActive,
      }).unwrap();
    } catch (err) {
      setStatusError(getErrorMessage(err, "Failed to update discount rule status."));
    }
  };

  if (!selectedStoreId) {
    return (
      <div className="space-y-6">
        <PageHeader title="Discount Rules" subtitle="Manage product and store-wide discounts." />
        <Card>
          <Alert tone="warning">Please select a store to manage discount rules.</Alert>
        </Card>
      </div>
    );
  }

  const columns = [
    { key: "name", label: "Name" },
    { key: "scope", label: "Scope" },
    { key: "product", label: "Product" },
    { key: "type", label: "Type" },
    { key: "value", label: "Value" },
    { key: "active", label: "Active" },
    { key: "window", label: "Window" },
    { key: "updated", label: "Updated" },
    { key: "actions", label: "Actions" },
  ];

  const tableRows = rowsForDisplay.map((rule) => {
    const id = getRuleId(rule);
    const isProductRule = rule.discountScope === DISCOUNT_SCOPES.PRODUCT;
    return {
      id,
      name: <div className="font-semibold text-slate-900">{rule.name || "Unnamed rule"}</div>,
      scope: (
        <Badge tone={isProductRule ? "info" : "neutral"}>
          {isProductRule ? "Product" : "Store-wide"}
        </Badge>
      ),
      product: isProductRule ? itemNameById.get(String(rule.itemId)) || "Unknown product" : "All products",
      type: rule.discountType === DISCOUNT_TYPES.PERCENTAGE ? "Percentage" : "Fixed Amount",
      value: formatRuleValue(rule, currencyCode),
      active: <Badge tone={rule.isActive ? "success" : "danger"}>{rule.isActive ? "Active" : "Inactive"}</Badge>,
      window: (
        <div className="text-sm text-slate-600">
          <div>{rule.startsAt ? formatDate(rule.startsAt) : "No start"}</div>
          <div>{rule.endsAt ? formatDate(rule.endsAt) : "No end"}</div>
        </div>
      ),
      updated: formatDate(rule.updatedAt || rule.createdAt),
      actions: (
        <div className="flex items-center gap-2">
          <ActionButton label="Edit" icon="edit" onClick={() => openEdit(rule)} />
          <ActionButton
            label={rule.isActive ? "Deactivate" : "Activate"}
            icon="toggle"
            checked={Boolean(rule.isActive)}
            tone={rule.isActive ? "danger" : "success"}
            onClick={() => toggleRuleStatus(rule)}
            disabled={statusState.isLoading}
          />
        </div>
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Discount Rules"
        subtitle="Manage product-specific and store-wide discounts."
        actions={<Button size="sm" onClick={openCreate}>Add discount rule</Button>}
      />

      <Card>
        <div className="grid gap-4 lg:grid-cols-5">
          <Select label="Scope" value={filters.scope} onChange={(event) => setFilterValue("scope", event.target.value)}>
            <option value="">All scopes</option>
            <option value={DISCOUNT_SCOPES.PRODUCT}>Product</option>
            <option value={DISCOUNT_SCOPES.STORE_WIDE}>Store-wide</option>
          </Select>
          <Select label="Product" value={filters.itemId} onChange={(event) => setFilterValue("itemId", event.target.value)} disabled={itemsLoading}>
            <option value="">All products</option>
            {items.map((item) => (
              <option key={getItemId(item)} value={getItemId(item)}>
                {getItemName(item)}
              </option>
            ))}
          </Select>
          <Select label="Active" value={filters.isActive} onChange={(event) => setFilterValue("isActive", event.target.value)}>
            <option value="">Any status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </Select>
          <Select label="Effective" value={filters.currentlyEffective} onChange={(event) => setFilterValue("currentlyEffective", event.target.value)}>
            <option value="">Any time</option>
            <option value="true">Currently effective</option>
            <option value="false">Not currently effective</option>
          </Select>
          <Input label="Search" type="search" value={filters.search} onChange={(event) => setFilterValue("search", event.target.value)} placeholder="Rule or product" />
        </div>
        <div className="mt-4 flex justify-end">
          <Button type="button" size="sm" variant="secondary" onClick={resetFilters}>Reset</Button>
        </div>
      </Card>

      <Card className="p-0">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="text-sm font-semibold text-slate-700">All discount rules</div>
          {isFetching && <div className="text-xs font-medium text-slate-500">Refreshing...</div>}
        </div>
        <div className="px-6 py-4">
          {statusError && <div className="mb-4"><Alert tone="error">{statusError}</Alert></div>}
          {isLoading && <p className="text-sm text-slate-500">Loading discount rules...</p>}
          {isError && <Alert tone="error">{getErrorMessage(error, "Failed to load discount rules.")}</Alert>}
          {!isLoading && !isError && (
            <>
              <Table columns={columns} rows={tableRows} emptyText="No discount rules found." />
              <Pagination
                page={page}
                pageSize={pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                totalCount={totalCount}
                isLoading={isFetching}
                onPageChange={setPage}
                onPageSizeChange={(nextPageSize) => {
                  setPage(1);
                  setPageSize(nextPageSize);
                }}
              />
              <div className="mt-2 text-xs text-slate-500">Page {page} of {totalPages}</div>
            </>
          )}
        </div>
      </Card>

      <Modal isOpen={isFormOpen} onClose={closeForm} title={editingRuleId ? "Edit discount rule" : "Add discount rule"} size="lg">
        <DiscountRuleForm
          form={form}
          setForm={setForm}
          items={items}
          currencyCode={currencyCode}
          validationError={formError}
          mutationState={editingRuleId ? updateState : createState}
          onSubmit={saveRule}
          onCancel={closeForm}
          isEditing={Boolean(editingRuleId)}
        />
      </Modal>
    </div>
  );
};

export default DiscountRules;